import { createTheme } from '@mui/material/styles';

export const brandColors = {
  primary: '#7B1FA2',
  primaryDark: '#5E167A',
  primaryLight: '#9C27B0',
  accent: '#BA68C8',
  background: '#0F0F10',
  surface: '#1B1B1B',
  surfaceAlt: '#121212',
  border: '#2F2F2F',
  text: '#FFFFFF',
  mutedText: '#8A8D91',
} as const;

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: brandColors.primary,
      dark: brandColors.primaryDark,
      light: brandColors.primaryLight,
      contrastText: brandColors.text,
    },
    secondary: {
      main: brandColors.mutedText,
      dark: brandColors.surface,
      light: '#B8B8B8',
      contrastText: brandColors.text,
    },
    background: {
      default: brandColors.background,
      paper: brandColors.surface,
    },
    text: {
      primary: brandColors.text,
      secondary: brandColors.mutedText,
      disabled: '#5F6267',
    },
    divider: brandColors.border,
  },
  typography: {
    fontFamily: "'Outfit', sans-serif",
    body1: {
      color: brandColors.mutedText,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: brandColors.background,
          color: brandColors.text,
        },
        '#root': {
          backgroundColor: brandColors.background,
          color: brandColors.text,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.surface,
          backgroundImage: 'none',
          color: brandColors.text,
          borderBottom: `1px solid ${brandColors.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: "'Outfit', sans-serif",
          fontSize: '14px',
          color: brandColors.text,
          borderColor: brandColors.border,
        },
        head: {
          color: brandColors.text,
          backgroundColor: brandColors.surfaceAlt,
          fontWeight: 700,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(123, 31, 162, 0.08)',
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': {
            outline: `2px solid ${brandColors.primaryLight}`,
            outlineOffset: 2,
          },
          '& .MuiTouchRipple-child': {
            backgroundColor: 'rgba(186, 104, 200, 0.55)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          '&:active': {
            backgroundColor: brandColors.primaryDark,
            boxShadow: '0 0 0 3px rgba(123, 31, 162, 0.24)',
          },
          '&.Mui-focusVisible': {
            boxShadow: '0 0 0 3px rgba(156, 39, 176, 0.32)',
          },
        },
        containedPrimary: {
          backgroundColor: brandColors.primary,
          color: brandColors.text,
          '&:hover': {
            backgroundColor: brandColors.primaryDark,
          },
          '&:active': {
            backgroundColor: brandColors.primaryDark,
          },
        },
        outlinedPrimary: {
          borderColor: brandColors.primary,
          color: brandColors.text,
          '&:hover': {
            borderColor: brandColors.primaryLight,
            backgroundColor: 'rgba(123, 31, 162, 0.12)',
          },
          '&:active': {
            borderColor: brandColors.primaryLight,
            backgroundColor: 'rgba(123, 31, 162, 0.2)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.surfaceAlt,
          color: brandColors.text,
          borderRadius: 12,
          '& .MuiInputBase-input': {
            color: brandColors.text,
          },
          '& .MuiInputLabel-root': {
            color: brandColors.mutedText,
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '& fieldset': {
              borderColor: brandColors.border,
            },
            '&:hover fieldset': {
              borderColor: brandColors.primary,
            },
            '&.Mui-focused fieldset': {
              borderColor: brandColors.primary,
            },
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: brandColors.text,
          backgroundColor: brandColors.surface,
          borderColor: brandColors.border,
          '&.Mui-selected': {
            backgroundColor: brandColors.primary,
            color: brandColors.text,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: brandColors.surface,
          color: brandColors.text,
          borderColor: brandColors.border,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: brandColors.surface,
          border: `1px solid ${brandColors.border}`,
          color: brandColors.text,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: brandColors.surface,
          color: brandColors.text,
          borderRight: `1px solid ${brandColors.border}`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: brandColors.surface,
          color: brandColors.text,
          border: `1px solid ${brandColors.border}`,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: brandColors.text,
          '&:hover': {
            backgroundColor: 'rgba(123, 31, 162, 0.14)',
          },
          '&:active': {
            backgroundColor: 'rgba(123, 31, 162, 0.24)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          color: brandColors.text,
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: 'rgba(123, 31, 162, 0.18)',
            color: brandColors.text,
          },
          '&:hover': {
            backgroundColor: 'rgba(123, 31, 162, 0.1)',
          },
          '&:active': {
            backgroundColor: 'rgba(123, 31, 162, 0.24)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: brandColors.primary,
          color: brandColors.text,
        },
        outlinedPrimary: {
          borderColor: brandColors.primary,
          color: brandColors.text,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: brandColors.primary,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: brandColors.text,
          textTransform: 'none',
          '&.Mui-selected': {
            color: brandColors.text,
            backgroundColor: brandColors.primary,
          },
        },
      },
    },
  },
});
